/** `sections/product-carousel.liquid`
 *  - `<product-carousel-group>`: wraps one `<product-carousel>` rail per shipping-group
 *    block and shows only the rail whose shipping group includes the visitor's state.
 *  - `<product-carousel>`: horizontal product rail with ScrollBooster-powered
 *    drag/trackpad scrolling and card-by-card arrow navigation.
 */

import ScrollBooster from 'scrollbooster'
import { CarouselPagination } from './carousel-pagination.js'

const REGION_LS_KEY = 'wyld_us_state'
const REGION_CAROUSEL_SELECTOR = '[data-region-carousel]'

const VIEWPORT_SELECTOR = '[data-product-carousel-viewport]'
const TRACK_SELECTOR = '[data-product-carousel-track]'
const CARD_SELECTOR = '[data-product-card]'
const PREV_SELECTOR = '[data-product-carousel-prev]'
const NEXT_SELECTOR = '[data-product-carousel-next]'
const PAGINATION_SELECTOR = '[data-product-carousel-pagination]'
const GRAB_CLASS = 'is-grabbing'

class ProductCarousel extends HTMLElement {
  #abort
  #booster
  #viewport
  #track
  #prevBtn
  #nextBtn
  #pagination
  #resizeObserver
  #caHidden = []

  connectedCallback() {
    this.#viewport = this.querySelector(VIEWPORT_SELECTOR)
    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#prevBtn = this.querySelector(PREV_SELECTOR)
    this.#nextBtn = this.querySelector(NEXT_SELECTOR)
    if (!this.#viewport || !this.#track) return

    this.#abort = new AbortController()
    const { signal } = this.#abort

    // Autoplay is opt-in per section via `data-autoplay`. When enabled, the delay
    // (ms) comes from `data-slideshow-timeout`, falling back to
    // CarouselPagination's default when the attribute is missing/invalid.
    const autoplayEnabled = this.dataset.autoplay === 'true'
    const autoplay =
      autoplayEnabled && (Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || true)

    this.#pagination = new CarouselPagination(this.querySelector(PAGINATION_SELECTOR), {
      signal,
      onSelect: (x) => this.#booster?.scrollTo({ x }),
      autoplay,
      pauseOn: this,
    })

    this.#booster = new ScrollBooster({
      viewport: this.#viewport,
      content: this.#track,
      scrollMode: 'transform',
      direction: 'horizontal',
      pointerMode: 'touch',
      emulateScroll: true,
      preventDefaultOnEmulateScroll: 'horizontal',
      lockScrollOnDragDirection: 'horizontal',
      bounce: true,
      bounceForce: 0.1,
      textSelection: true,
      onPointerDown: () => this.#viewport.classList.add(GRAB_CLASS),
      onPointerUp: () => this.#viewport.classList.remove(GRAB_CLASS),
      onUpdate: (state) => this.#syncArrowState(state),
    })

    this.#prevBtn?.addEventListener('click', this.#onPrev, { signal })
    this.#nextBtn?.addEventListener('click', this.#onNext, { signal })

    if (this.dataset.caFilter != null) {
      this.#applyCaFilter()
      document.addEventListener('theme:region:state-changed', this.#applyCaFilter, { signal })
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#booster?.updateMetrics()
        this.#syncArrowState()
      })
      this.#resizeObserver.observe(this.#viewport)
      this.#resizeObserver.observe(this.#track)
    }

    this.#syncArrowState()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#resizeObserver?.disconnect()
    this.#pagination?.destroy()
    this.#booster?.destroy()
    this.#booster = undefined
  }

  // Opt-in via `data-ca-filter`: California visitors don't see products that are
  // unavailable in California. Cards are detached from the DOM (not just hidden
  // with `display: none`) so ScrollBooster's step/overflow math never measures
  // them; a placeholder comment remembers each card's slot so it can be restored
  // when the visitor leaves California. Re-runs on `theme:region:state-changed`
  // (region.js keeps `body.ca-active` in sync) so the rail updates live, and the
  // whole rail is hidden when no products remain.
  #applyCaFilter = () => {
    if (!this.#track) return
    let changed = false

    if (document.body.classList.contains('ca-active')) {
      this.#track
        .querySelectorAll(`${CARD_SELECTOR}[data-ca-available='false']`)
        .forEach((card) => {
          const placeholder = document.createComment('ca-hidden')
          card.before(placeholder)
          card.remove()
          this.#caHidden.push({ placeholder, card })
          changed = true
        })
    } else if (this.#caHidden.length) {
      this.#caHidden.forEach(({ placeholder, card }) => placeholder.replaceWith(card))
      this.#caHidden = []
      changed = true
    }

    this.hidden = !this.#track.querySelector(CARD_SELECTOR)
    this.refresh()
    // Let product-card.js re-apply region availability to the current cards
    // (e.g. restored cards need styling for the visitor's new region).
    if (changed) document.dispatchEvent(new CustomEvent('theme:cards:appended'))
  }

  // Recompute scroll metrics + arrow/pagination state. Called by the parent
  // `<product-carousel-group>` after revealing this rail, since a rail that was
  // `hidden` on connect initialised with a zero-size viewport.
  refresh() {
    this.#booster?.updateMetrics()
    this.#syncArrowState()
  }

  #onPrev = () => {
    this.#scrollByCards(-1)
  }

  #onNext = () => {
    this.#scrollByCards(1)
  }

  #scrollByCards(direction) {
    if (!this.#booster) return
    const step = this.#cardStep()
    const current = this.#booster.getState().position.x
    const max = this.#maxScroll()
    const target = Math.max(0, Math.min(max, current + direction * step))
    this.#booster.scrollTo({ x: target })
  }

  #cardStep() {
    const cards = this.#track?.querySelectorAll(CARD_SELECTOR)
    if (!cards || cards.length === 0) return this.#viewport.clientWidth
    const first = cards[0]
    const second = cards[1]
    if (second) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left
    }
    const style = globalThis.getComputedStyle(this.#track)
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0
    return first.getBoundingClientRect().width + gap
  }

  #maxScroll() {
    if (!this.#viewport || !this.#track) return 0
    return Math.max(0, this.#track.scrollWidth - this.#viewport.clientWidth)
  }

  #syncArrowState(state) {
    if (!this.#booster) return
    const current = (state ?? this.#booster.getState()).position.x
    const max = this.#maxScroll()
    const atStart = current <= 1
    const atEnd = current >= max - 1
    const hasOverflow = max > 1

    if (this.#prevBtn) {
      this.#prevBtn.disabled = !hasOverflow || atStart
    }
    if (this.#nextBtn) {
      this.#nextBtn.disabled = !hasOverflow || atEnd
    }

    this.#pagination?.update({ current, max, step: this.#cardStep() })
  }
}

/**
 * Region-aware wrapper: each child `[data-region-carousel]` holds a rail for one
 * shipping-group block (with a `data-states` JSON array of the group's US
 * states). Shows the first rail whose states include the visitor's selected
 * state, falling back to the first rail when there's no state or no match.
 */
class ProductCarouselGroup extends HTMLElement {
  #abort
  #carousels = []

  connectedCallback() {
    this.#carousels = [...this.querySelectorAll(REGION_CAROUSEL_SELECTOR)]
    if (this.#carousels.length === 0) return

    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#applySelection()

    document.addEventListener('theme:region:state-changed', this.#onRegionChange, { signal })
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal })
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal })
  }

  disconnectedCallback() {
    this.#abort?.abort()
  }

  #readState() {
    try {
      return localStorage.getItem(REGION_LS_KEY)
    } catch {
      return null
    }
  }

  #statesFor(carousel) {
    try {
      const parsed = JSON.parse(carousel.dataset.states || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  #pick(state) {
    if (state) {
      const match = this.#carousels.find((c) => this.#statesFor(c).includes(state))
      if (match) return match
    }
    return this.#carousels[0]
  }

  #show(target) {
    for (const carousel of this.#carousels) {
      const active = carousel === target
      carousel.hidden = !active
      if (active) {
        carousel.querySelector('product-carousel')?.refresh?.()
      }
    }
  }

  #applySelection() {
    this.#show(this.#pick(this.#readState()))
  }

  #onRegionChange = (event) => {
    this.#show(this.#pick(event.detail?.state ?? this.#readState()))
  }

  // Theme editor: reveal whichever block the merchant is editing.
  #onBlockSelect = (event) => {
    const selected = this.#carousels.find((c) => c.dataset.blockId === event.detail?.blockId)
    if (selected) this.#show(selected)
  }

  #onBlockDeselect = () => {
    this.#applySelection()
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel)
}

if (!customElements.get('product-carousel-group')) {
  customElements.define('product-carousel-group', ProductCarouselGroup)
}
